import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import { CUSTOM_STATS_CATALOGUE } from '@lib/page-utils/Opta/custom-stats';

const FFHModal = dynamic(() => import('@components/modal'));

/**
 * Past this many columns a custom table stops fitting comfortably on screen.
 * Guidance only: the hint appears but Save is never blocked.
 */
const COMFORTABLE_COLUMN_COUNT = 12;

type CustomStatsModalProps = {
  open: boolean;
  selectedPaths: string[];
  onSave: (paths: string[]) => void;
  onClose: () => void;
};

const selectionHintFor = (count: number) => {
  if (count === 0) return 'Pick at least one to build your table.';
  if (count > COMFORTABLE_COLUMN_COUNT)
    return `Tables stay readable up to about ${COMFORTABLE_COLUMN_COUNT} columns.`;
  return '';
};

/**
 * Column picker for the custom stats table. Edits are held as a draft and
 * only reach the page (and localStorage) on save, so cancelling never
 * disturbs the current table.
 */
const CustomStatsModal = ({
  open,
  selectedPaths,
  onSave,
  onClose
}: CustomStatsModalProps) => {
  const [draftPaths, setDraftPaths] = useState<string[]>(selectedPaths);

  useEffect(() => {
    if (open) setDraftPaths(selectedPaths);
  }, [open, selectedPaths]);

  const toggleDraftPath = (path: string) =>
    setDraftPaths((current) =>
      current.includes(path)
        ? current.filter((existing) => existing !== path)
        : [...current, path]
    );

  const selectedCount = draftPaths.length;
  const hint = selectionHintFor(selectedCount);

  return (
    <FFHModal
      open={open}
      handleClose={onClose}
      // Matches the visible heading below, so the dialog's accessible name
      // and its on-screen title read the same.
      title="Custom stats"
      hideCloseButton
      childrenClassName="flex flex-col w-[92vw] max-w-[92vw] sm:w-[640px] lg:w-[900px] !overflow-hidden !rounded-2xl"
    >
      <div className="flex flex-none items-start justify-between gap-6 border-b border-neutral-200 px-6 pt-5 pb-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-neutral-800">
            Custom stats
          </h2>
          <p className="font-open-sans text-[13px] text-neutral-600">
            Choose the columns for your table.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-cy="close-modal-button"
          className="-mt-1 -mr-1.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg
                     text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-800
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
        >
          <Icon name="Close" className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* min-h-0 lets the body, not the header or footer, absorb the squeeze
          when the modal meets FFHModal's 95vh cap on a short viewport. */}
      <div
        className="min-h-0 flex-1 max-h-[60vh] overflow-y-auto px-6 pt-1 pb-5 sm:max-h-[520px]"
        data-cy="opta-custom-stats-picker"
      >
        {CUSTOM_STATS_CATALOGUE.map((category) => {
          const selectedInCategory = category.options.filter((option) =>
            draftPaths.includes(option.path)
          ).length;

          return (
            <div key={category.label} className="flex flex-col">
              {/* Sticky so the category a row belongs to stays readable while
                  the long catalogue scrolls past. */}
              <div className="sticky top-0 z-[2] flex items-center gap-2.5 bg-white pt-4 pb-2">
                <span className="whitespace-nowrap font-sans text-[11px] font-semibold uppercase tracking-[0.09em] text-neutral-600">
                  {category.label}
                </span>
                <span className="h-px flex-1 bg-neutral-200" />
                {selectedInCategory > 0 && (
                  <span className="font-mono text-[11px] text-neutral-600">
                    {selectedInCategory}/{category.options.length}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3">
                {category.options.map((option) => {
                  const isChecked = draftPaths.includes(option.path);

                  return (
                    <label
                      key={option.path}
                      className="flex cursor-pointer select-none items-center gap-2.5 rounded-lg p-2
                                 transition-colors hover:bg-neutral-100"
                    >
                      {/* The native input carries the design's treatment via
                          @tailwindcss/forms rather than hiding behind a drawn
                          box. A visually-hidden input would be `position:
                          absolute` against the fixed modal, not this scroll
                          body, so focusing one on click scrolled the list to
                          a blank region. */}
                      <input
                        type="checkbox"
                        name={`custom-stat-${option.path}`}
                        className="h-[18px] w-[18px] flex-none cursor-pointer rounded-[5px]
                                   border-[1.5px] border-neutral-300 text-primary-600
                                   focus:ring-2 focus:ring-primary-600 focus:ring-offset-1"
                        checked={isChecked}
                        onChange={() => toggleDraftPath(option.path)}
                      />
                      <span
                        className={`font-open-sans text-sm ${
                          isChecked
                            ? 'font-semibold text-neutral-800'
                            : 'text-neutral-700'
                        }`}
                      >
                        {option.title ?? option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-none flex-col gap-3 border-t border-neutral-200 bg-neutral-100 px-6 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="whitespace-nowrap font-sans text-sm font-semibold text-neutral-800">
            {selectedCount === 1
              ? '1 stat selected'
              : `${selectedCount} stats selected`}
          </span>
          {hint && (
            <span className="truncate font-open-sans text-[13px] text-neutral-600">
              {hint}
            </span>
          )}
        </div>
        <div className="flex flex-none items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3.5 py-2 font-open-sans text-[15px] font-bold text-neutral-700
                       transition-colors hover:bg-neutral-200 hover:text-neutral-800"
          >
            Cancel
          </button>
          <Button
            variant="primary"
            onClick={() => onSave(draftPaths)}
            data-cy="opta-custom-stats-save"
            className="!px-[18px] !py-[9px] !text-[15px]"
          >
            Save
          </Button>
        </div>
      </div>
    </FFHModal>
  );
};

export default CustomStatsModal;
